import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BasicSearchProps } from './types';

const DEBOUNCE_DELAY = 300;

const BasicSearch: React.FC<BasicSearchProps> = ({
  searchTerm,
  placeholder = 'Search...',
  onSearchTermChange,
  onClearSearch,
}) => {
  const [inputValue, setInputValue] = useState(searchTerm);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleClearSearch = useCallback(() => {
    setInputValue('');

    console.log('Search term changed: cleared'); // Debugging log
    onSearchTermChange('');
    if (onClearSearch) {
      onClearSearch();
    }
  }, [onSearchTermChange, onClearSearch]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      console.log('Search term changed:', value); // Debugging log
      onSearchTermChange(value);
    }, DEBOUNCE_DELAY);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      console.log('Search term changed: enter:', inputValue); // Debugging log
      onSearchTermChange(inputValue);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return (
    <div style={styles.searchInputWrapperStyle}>
      <input
        type="text"
        placeholder={placeholder}
        style={styles.searchInputStyle}
        value={inputValue}
        onChange={e => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {inputValue && (
        <button onClick={handleClearSearch} style={styles.clearButtonStyle}>
          <X size={16} />
        </button>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  searchInputWrapperStyle: {
    position: 'relative',
    width: '100%',
  } as React.CSSProperties,
  searchInputStyle: {
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    padding: '0 0.5rem',
    width: '100%',
    height: '1.875rem',
    boxSizing: 'border-box',
    fontSize: '0.8125rem',
  } as React.CSSProperties,
  clearButtonStyle: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    fontSize: '1rem',
  } as React.CSSProperties,
};

export default BasicSearch;
